<template>
    <div id="modal-create-tag">
        
        <div class="rp-overlay"></div>

        <div class="rp-modal" style="width: 530px;">

            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Combine tags') }}</h3>
                <button class="rp-btn-dismiss" @click="$emit('closeModal')">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>

            <div class="bg-white p-5">
				<!-- Tags -->
				<div class="flex flex-col sm:flex-row">
					
					<!-- Tags list -->
					<div class="sm:w-1/2 sm:pr-4 border-r">

						<div class="font-medium text-sm mb-2">{{ $t('Select Tags to Combine') }}</div>
						<div style="max-height:400px; overflow-x: hidden; overflow-y: auto; scrollbar-width: none; -ms-overflow-style: none;">
							<div v-for="(tag, id) in filteredTags" :key="id" 
								class="flex items-center mb-2 cursor-pointer hover:bg-blue-100 p-2 rounded-sm"
								@click="toggleCombineTag(tag)">
								<input
									type="checkbox"
									:id="'tag-' + id"
									:value="tag"
									v-model="layout.combineTags"
									class="mr-2 cursor-pointer"
									@click.stop/>
								<label class="cursor-pointer">{{ tag.name }}</label>
								<div class="w-4 h-4 ml-auto rounded-sm" :style="{ backgroundColor: tag.color }"></div>
							</div>
						</div>

					</div>

					<!-- Target tag -->
					<div class="sm:w-1/2 sm:pl-4 mt-4 sm:mt-0">

						<div class="font-medium text-sm mb-2">{{ $t('Resulting Tag') }}</div>

						<select v-model="layout.combineTargetTag" class="form-control mb-4">
							<option disabled value="">{{ $t('Select tag or create new') }}</option>
							<option
								v-for="(tag, id) in filteredTags" :key="id" :value="tag">
									{{ tag.name }}
							</option>
							<option value="new">{{ $t('New Tag') }}</option>
						</select>

						<div v-if="layout.combineTargetTag === 'new'">
							<input
								type="text"
								v-model="layout.inputTagName"
								:placeholder="$t('Enter new tag name')"
								class="rp-input-text w-full"/>
								<div v-if="!enableEditButton" class="text-red-500 text-sm mt-1">
									{{ errorMessage }}
								</div>
						</div>

					</div>

				</div>
                <!-- Action buttons -->
                <div class="rp-modal-body-buttons gap-3 mt-4" style="justify-content: center;">
                    <button class="rp-btn-secondary" @click="emit('closeModal')">
                        {{ $t('Cancel') }}
                    </button>
                    <button class="rp-btn-primary" @click="combineTags()"
                        :disabled="!enableCombineButton">
                        {{ $t('Combine') }}
                    </button>
                </div>
            </div>
            
        </div>

    </div>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue';
import { helper as $helper }        from "@/utils/helper";
import * as rosepetalModel	        from "rosepetal-model";

let $dataset = rosepetalModel.default.dataset;

const emit = defineEmits(['closeModal', 'tagsCombined']);

const props = defineProps({
    dataset: {
        type: Object,
        required: true,
    }
});

const layout = ref({
    dataset:            {},
    inputTagName:       '',
    combineTargetTag:   '',
    combineTags:        [],
});

const filteredTags = computed(() => {
    if (layout.value.dataset) {
		if (layout.value.dataset.type === 'MULTICLASS' || layout.value.dataset.type === 'imageObjectDetection') {
			return Object.entries(layout.value.dataset.tags).reduce((acc, [id, tag]) => {
                if (!tag?.unclassified) { acc[id] = tag; }
                return acc;
            }, {});
		} else if (layout.value.dataset.type === 'MULTILABEL') {
			return Object.entries(layout.value.dataset.tags).reduce((acc, [id, tag]) => {
				if (!tag?.anomaly && !tag?.normal) { acc[id] = tag; }
				return acc;
			}, {});
		}
    }
    return {};
});

const reservedNames = computed(() => {
    const type = layout.value.dataset.type;
    if (type === 'MULTICLASS' || type === 'imageObjectDetection') {
        return ["0", "unclassified"];
    } else if (type === 'MULTILABEL') {
        return ["0", "anomaly", "ok", "normal"];
    }
    return [];
});

const checkReservedNames = (name) => {
	return reservedNames.value.includes(name);
};

const checkDuplicatedName = (name) => {
    const tags = layout.value.dataset.tags || {};
    return Object.values(tags).some(tag => tag.id.toLowerCase() == name.toLowerCase());
};

const isValidTagName = (name) => {
    return !(checkReservedNames(name) || checkDuplicatedName(name));
};

const enableCombineButton = computed(() => {
    const { combineTags, combineTargetTag, inputTagName } = layout.value;
    if (!combineTags.length || !combineTargetTag) return false;
    if (combineTargetTag === 'new') {
        const name = inputTagName?.trim();
        return !!name && isValidTagName(name);
    }
    return true;
});

const errorMessage = computed(() => {
    const currentName = layout.value.inputTagName;
    if (!currentName) return "";
    if (!currentName.trim()) return "Invalid tag name";
    if (checkReservedNames(currentName)) return "Tag name is reserved";
    if (checkDuplicatedName(currentName)) return "Tag already exists";
    return "";
});

const toggleCombineTag = (tag) => {
	const index = layout.value.combineTags.indexOf(tag);
	if (index === -1) {
		layout.value.combineTags.push(tag);
	} else {
		layout.value.combineTags.splice(index, 1);
	}
}

const combineTags = async () => {
    const mainTag = layout.value.combineTargetTag === 'new' ? layout.value.inputTagName : layout.value.combineTargetTag;
	await $dataset.combineTags(layout.value.dataset, mainTag, layout.value.combineTags);
	$helper.NotificationTxt({ text: "Successfully combined", position: "center" });
	emit("tagsCombined");
}

onMounted(async () => { 
    if (props.dataset) {
        layout.value.dataset = props.dataset;
    }
});
</script>